<template>
  <el-dialog
    :modal="false"
    class="form-modal"
    :fullscreen="true"
    :show-close="false"
    :visible.sync="visible"
    v-loading="processing"
  >
    <div class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">
          {{
            context === "Edit" ? "Edit Exception" : "Add New Exception"
          }}
        </h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="handleSubmit">{{
            context
          }}</el-button>
          <el-button type="default" class="hidden-xs-only" @click="handleClose">
            Cancel</el-button
          >
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="handleClose"
          />
        </div>
      </div>
    </div>

    <el-form>
      <div class="inline-form-group">
        <el-col :span="12">
          <el-input v-model="form.name" placeholder="Name">
            <template slot="prefix">Name</template>
          </el-input></el-col
        >
        <el-col :span="1"></el-col>
        <el-col :span="12">
          <el-input
            v-model.number="form.notifyDaysBefore"
            type="number"
            placeholder="Days Before Notice"
          >
            <template slot="prefix">Days Before Notice</template>
          </el-input>
        </el-col>
      </div>

      <h2 class="form-section-header">Date Range</h2>
      <div class="inline-from-group mt-5 mb-5">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          format="MMM d, yyyy"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          :clearable="false"
        >
        </el-date-picker>
      </div>

      <div v-if="sameDatesSelected" class="inline-form-group">
        <el-col :span="14">
          <dads-select
            v-model="recurrencePattern"
            @change="onRecurrencePatternSelection"
            placeholder="Select recurrence pattern"
          >
            <template slot="prefix">Recurrence Pattern</template>

            <el-option
              v-for="(pattern, index) in recurrencePatterns"
              :key="index"
              :label="pattern.label"
              :value="pattern.id"
            />
          </dads-select>
        </el-col>
      </div>

      <div class="inline-form-group pb-5 pt-10">
        <el-switch v-model="form.isClosed"></el-switch>
        <span class="black-text ml-10">Closed on this day</span>
      </div>

      <multi-time-range-selector
        v-if="!form.isClosed"
        :disabled="false"
        :values="operatingHours"
        @timeRangeSelected="onTimeRangeSelection"
      />

      <existing-mediafile
        title="Audio File"
        :clearState="false"
        :selectedMeId="form.mediaFileId"
        @mediafileSelected="onMediaFileSelected"
      />
    </el-form>
  </el-dialog>
</template>

<script>
import { DateTime } from "luxon";
import DadsSelect from "@/components/select";
import ExistingMediafile from "@/components/ExistingMediafile";
import MultiTimeRangeSelector from "./multiTimeRangeSelector.vue";
import { createHoliday, editHoliday } from "@/api/holidays/methods.js";

export default {
  name: "HolidaysForm",

  props: {
    context: { required: true },
    visible: { required: true },
    selectedRecord: { required: true },
    businessHoursId: { required: true },
    getRecurrencePatternText: { required: true }
  },

  components: {
    DadsSelect,
    ExistingMediafile,
    MultiTimeRangeSelector
  },

  data() {
    return {
      processing: false,

      operatingHours: [
        {
          startTime: "09:00",
          endTime: "17:00"
        }
      ],
      recurrencePatterns: [],
      recurrencePattern: "x-x-x-x",
      dateRange: [DateTime.now().toJSDate(), DateTime.now().toJSDate()],

      form: {
        name: "",
        day: null,
        month: null,
        dayOfWeek: null,
        isClosed: false,
        weekOfMonth: null,
        notifyDaysBefore: 7,
        mediaFileId: null
      }
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    sameDatesSelected() {
      const [d1, d2] = this.dateRange;
      if (!d1 || !d2) return false;

      const date1 = DateTime.fromJSDate(d1);
      const date2 = DateTime.fromJSDate(d2);

      if (!date1.isValid || !date2.isValid) return false;

      return (
        date1.year === date2.year &&
        date1.month === date2.month &&
        date1.day === date2.day
      );
    }
  },

  watch: {
    selectedRecord: {
      immediate: true,
      handler(record) {
        if (record) {
          for (const [key, value] of Object.entries(record)) {
            if (key in this.form) {
              this.form[key] = value;
            }
          }

          this.form.mediaFileId =
            record.mediaFile && record.mediaFile.id ? record.mediaFile.id : null;

          const {
            startDate,
            endDate,
            operatingHours,
            isRecurring,
            day,
            month,
            weekOfMonth,
            dayOfWeek
          } = record;

          if (operatingHours && operatingHours.length) {
            this.operatingHours = operatingHours;
          }

          if (startDate && endDate) {
            this.dateRange = null;
            this.dateRange = [];
            this.dateRange[0] = DateTime.fromISO(startDate).toJSDate();
            this.dateRange[1] = DateTime.fromISO(endDate).toJSDate();
          } else if (isRecurring) {
            const currentYear = DateTime.now().year;
            const currentMonth = DateTime.now().month;

            const hasMonth = month != null;
            const hasDay = day != null;
            const hasDayOfWeek = dayOfWeek != null;
            const hasWeekOfMonth = weekOfMonth != null;

            // Create a date based on the recurrence pattern
            let newDate;

            // ⭐️ Pattern 1: Month + Day (Annually on May 20th), (e.g., Christmas - Dec 25) ⭐️ //
            if (hasDay && hasMonth && !hasDayOfWeek && !hasWeekOfMonth) {
              newDate = DateTime.fromObject({ year: currentYear, month, day });
            }

            // ⭐️ Pattern 2: Month + DayOfWeek + WeekOfMonth (Annually on the 3rd Tuesday of May) ⭐️ //
            else if (hasMonth && hasDayOfWeek && hasWeekOfMonth && !hasDay) {
              newDate = this.calculateFloatingHolidayDate(
                currentYear,
                month,
                dayOfWeek,
                weekOfMonth
              );
            }

            // ⭐️ Pattern 3: Day (Monthly on the 3rd) ⭐️ //
            else if (!hasMonth && hasDay && !hasDayOfWeek && !hasWeekOfMonth) {
              const currentMonth = DateTime.now().month;
              // Create a DateTime for the specified day in the current month
              newDate = DateTime.fromObject({
                year: currentYear,
                month: currentMonth,
                day
              });
            }

            // ⭐️ Pattern 4: WeekOfMonth + DayOfWeek (Monthly on the 3rd Tuesday) ⭐️ //
            else if (!hasMonth && hasDayOfWeek && hasWeekOfMonth && !hasDay) {
              newDate = this.calculateFloatingHolidayDate(
                currentYear,
                currentMonth,
                dayOfWeek,
                weekOfMonth
              );
            }

            // ⭐️ Pattern 5: DayOfWeek (Weekly on Tuesday) ⭐️ //
            else if (!hasMonth && !hasDay && hasDayOfWeek && !hasWeekOfMonth) {
              newDate = this.getDateForDayOfWeek(dayOfWeek);
            } else {
              newDate = DateTime.now();
            }

            const jsDate = newDate.toJSDate();
            this.dateRange = [jsDate, jsDate];
          }

          this.generateRecurrencePatterns();

          const patternId = this.generateRecurrencePatternId(
            this.selectedRecord
          );
          this.recurrencePattern = patternId;

          const patternExists =
            this.recurrencePatterns.findIndex((p) => p.id === patternId) !== -1;

          if (!patternExists) {
            const { day, dayOfWeek, weekOfMonth, month } = record;
            this.recurrencePatterns.push({
              id: patternId,
              value: {
                day,
                dayOfWeek,
                weekOfMonth,
                month
              },
              label: this.getRecurrencePatternText(record)
            });
          }
        }
      }
    },

    visible(visible) {
      if (visible && this.context === "Create") {
        this.generateRecurrencePatterns();
      }
    },

    dateRange() {
      const previousPatternId = this.recurrencePattern;

      if (this.sameDatesSelected) {
        this.generateRecurrencePatterns();

        // After generating new patterns, check if the previous pattern still exists
        const patternExists = this.recurrencePatterns.some(
          (p) => p.id === previousPatternId
        );

        // If the pattern no longer exists in the list, reset to "Does not repeat"
        if (!patternExists) {
          this.recurrencePattern = "x-x-x-x"; // The ID for "Does not repeat"
        }
      }

      if (!this.sameDatesSelected) {
        this.form.day = null;
        this.form.month = null;
        this.form.dayOfWeek = null;
        this.form.weekOfMonth = null;
      }
    }
  },

  methods: {
    getDateForDayOfWeek(dayOfWeek) {
      const now = DateTime.now();
      const startOfWeek = now.startOf("week");
      const targetDay = this.convertToLuxonDayOfWeek(dayOfWeek);

      // We are subtracting 1 from the target to account for the current day
      return startOfWeek.plus({ days: targetDay - 1 });
    },
    getLastDayOfWeekInMonth(year, month, dayOfWeek) {
      const luxonDayOfWeek = this.convertToLuxonDayOfWeek(dayOfWeek);

      // Get the last day of the month
      let lastDay = DateTime.fromObject({
        year,
        month: month + 1,
        day: 1
      }).minus({ days: 1 });

      // Go backward to find the last occurrence of the target day of week
      while (lastDay.weekday !== luxonDayOfWeek) {
        lastDay = lastDay.minus({ days: 1 });
      }

      return lastDay;
    },
    convertToLuxonDayOfWeek(dayOfWeek) {
      return dayOfWeek === 0 ? 7 : dayOfWeek;
    },
    calculateFloatingHolidayDate(year, month, dayOfWeek, weekOfMonth) {
      // Find the first day of the specified month
      let dt = DateTime.fromObject({ year, month, day: 1 });

      // Convert to Luxon day format
      const luxonDayOfWeek = this.convertToLuxonDayOfWeek(dayOfWeek);

      // Find first occurrence of the day in the month
      while (dt.weekday !== luxonDayOfWeek) {
        dt = dt.plus({ days: 1 });
      }

      // Add weeks to get to the desired week of month
      dt = dt.plus({ weeks: weekOfMonth - 1 });

      if (dt.month !== month) {
        dt = dt.minus({ weeks: 1 });
      }

      return dt;
    },
    onRecurrencePatternSelection(id) {
      const pattern = this.recurrencePatterns.find((p) => p.id === id);
      if (pattern.value) {
        const { day, dayOfWeek, weekOfMonth, month } = pattern.value;

        this.form.day = day;
        this.form.month = month;
        this.form.dayOfWeek = dayOfWeek;
        this.form.weekOfMonth = weekOfMonth;
      }
    },

    generateRecurrencePatternId({ day, dayOfWeek, weekOfMonth, month }) {
      return [day, dayOfWeek, weekOfMonth, month]
        .map((v) => (v == null ? "x" : v))
        .join("-");
    },

    generateRecurrencePatterns() {
      this.recurrencePatterns = [];

      const patterns = [
        {
          label: "Does not repeat",
          value: {
            day: null,
            dayOfWeek: null,
            weekOfMonth: null,
            month: null
          }
        }
      ];

      // Ensure we have a valid start date before proceeding
      if (this.sameDatesSelected) {
        const [startDate] = this.dateRange;
        const date = DateTime.fromJSDate(startDate);

        // Get date components
        const day = date.day;
        const weekdayName = date.toFormat("EEEE"); // Monday, Tuesday, etc.
        let weekOfMonth = Math.ceil(day / 7); // Calculate which week of the month

        // Check if this is the last occurrence of this weekday in the month
        const isLastOccurrence = this.isLastWeekdayOfMonth(date);

        const ordinalSuffix = this.getOrdinalSuffix(weekOfMonth);
        const monthName = date.toFormat("MMMM"); // January, February, etc.

        // Create recurrence patterns based on date components
        const validPatterns = [
          // Weekly patterns
          {
            label: `Weekly on ${weekdayName}`,
            value: {
              day: null,
              dayOfWeek: date.weekday % 7,
              weekOfMonth: null,
              month: null
            }
          },

          // Monthly patterns by weekday position
          // Conditional 4th weekday pattern
          ...(weekOfMonth === 4 && isLastOccurrence
            ? [
                {
                  label: `Monthly on the ${ordinalSuffix} ${weekdayName}`,
                  value: {
                    day: null,
                    dayOfWeek: date.weekday % 7,
                    weekOfMonth: weekOfMonth,
                    month: null
                  }
                }
              ]
            : []),

          {
            label: `Monthly on the ${
              isLastOccurrence ? "last" : ordinalSuffix
            } ${weekdayName}`,
            value: {
              day: null,
              dayOfWeek: date.weekday % 7,
              weekOfMonth: isLastOccurrence ? 5 : weekOfMonth,
              month: null
            }
          },

          // Yearly patterns
          {
            label: `Annually on ${monthName} ${this.getOrdinalSuffix(day)}`,
            value: {
              day: day,
              dayOfWeek: null,
              weekOfMonth: null,
              month: date.month
            }
          },
          {
            label: `Annually on the ${
              isLastOccurrence ? "last" : ordinalSuffix
            } ${weekdayName} of ${monthName}`,
            value: {
              day: null,
              dayOfWeek: date.weekday % 7,
              weekOfMonth: isLastOccurrence ? 5 : weekOfMonth,
              month: date.month
            }
          }
        ];

        patterns.push(...validPatterns);
      }

      this.recurrencePatterns = patterns.map((p) => {
        const id = this.generateRecurrencePatternId(p.value);
        return {
          id,
          ...p
        };
      });
    },

    isLastWeekdayOfMonth(date) {
      // Check if adding 7 days would put us in the next month
      const nextWeek = date.plus({ days: 7 });
      return nextWeek.month !== date.month;
    },

    getOrdinalSuffix(num) {
      const j = num % 10;
      const k = num % 100;

      if (j === 1 && k !== 11) {
        return num + "st";
      }
      if (j === 2 && k !== 12) {
        return num + "nd";
      }
      if (j === 3 && k !== 13) {
        return num + "rd";
      }
      return num + "th";
    },

    onMediaFileSelected(mediaFileId) {
      this.form.mediaFileId = mediaFileId;
    },

    onTimeRangeSelection(ranges) {
      this.operatingHours = ranges;
    },

    async createOperation(body) {
      try {
        await this.$confirm("Create new exception?", "Warning", {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel"
        });

        this.processing = true;
        const { success } = await createHoliday({ body });
        this.processing = false;

        if (success) {
          this.cancelOperation();
        }
      } catch (err) {
        this.processing = false;
      }
    },

    async editOperation(body) {
      try {
        await this.$confirm("Edit this exception?", "Warning", {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel"
        });

        this.processing = true;

        const { success } = await editHoliday({
          id: this.selectedRecord.id,
          body
        });

        this.processing = false;

        if (success) {
          this.cancelOperation();
        }
      } catch (err) {
        this.processing = false;
      }
    },

    cancelOperation() {
      this.clearForm();
      this.$emit("cancelOperation");
    },

    handleClose() {
      this.clearForm();
      this.$emit("handleClose");
    },

    handleSubmit() {
      const { day, dayOfWeek, weekOfMonth, month } = this.form;

      if (
        this.form.notifyDaysBefore > 0 &&
        !this.form.mediaFileId
      ) {
        this.$notify({
          title: "No audio file selected",
          message:
            "Prior days notice is set, but nothing will play without an audio file.",
          type: "warning"
        });
      }

      let isRecurring = false;
      if (this.sameDatesSelected) {
        isRecurring = !!(day || dayOfWeek || weekOfMonth || month);
      }

      this.sameDatesSelected;

      const body = {
        isRecurring,
        ...this.form,
        operatingHours: this.form.isClosed ? null : this.operatingHours
      };

      if (isRecurring) {
        body.startDate = null;
        body.endDate = null;
      }

      if (!isRecurring) {
        const [startDate, endDate] = this.dateRange;
        body.startDate = startDate
          ? DateTime.fromJSDate(startDate).toISODate()
          : null;
        body.endDate = endDate
          ? DateTime.fromJSDate(endDate).toISODate()
          : null;
      }

      if (this.context === "Create") {
        body.tenantId = this.selectedTenant.id;
        body.businessHoursId = this.businessHoursId;
        this.createOperation(body);
        return;
      }

      this.editOperation(body);
    },

    clearForm() {
      this.operatingHours = [
        {
          startTime: "09:00",
          endTime: "17:00"
        }
      ];

      this.recurrencePattern = "x-x-x-x";
      this.recurrencePatterns = [];

      this.dateRange = [DateTime.now().toJSDate(), DateTime.now().toJSDate()];

      this.form = {
        name: "",
        day: null,
        month: null,
        dayOfWeek: null,
        isClosed: false,
        weekOfMonth: null,
        notifyDaysBefore: 7,
        mediaFileId: null
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.el-form {
  max-width: 600px;
  margin: 20px auto 0 auto;
}
</style>
